import React, { useState } from 'react';

import { Tooltip, Fab } from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  DeleteForever
} from "@mui/icons-material";

import { 
  AttachedTable,
  Potence,
  UpgradableNumber
} from "@dndModels";
import { ABILITY_SCORES, CHARACTER_LEVELS } from "@dndModels/Constants";

import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import StringBox from "@components/input/strings/StringBox";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import UpgradableNumberBox from "@dndComponents/UpgradableNumberBox";
import { AbilityScoreType } from "@dndModels/Types";

interface Props {
  obj: Potence;
  disabled?: boolean;
  onChange: (changed: Potence) => void;
  onDelete: () => void;
  attached_table?: AttachedTable;
}

export default function AbilityPotenceInput({ 
  obj,
  onChange,
  onDelete,
  disabled = false,
  attached_table
}: Props) {
  const [basic, setBasic] = useState(obj.rolls.flat.isBasic() && obj.rolls.count.isBasic() && obj.rolls.size.isBasic());

  return (
    <Grid
      container
      spacing={0}
      direction="row"
      size={{
        xs: 12
      }}>
      <Grid
        size={{
          xs: 1
        }}>
        <SelectStringBox 
          name="Level"
          options={CHARACTER_LEVELS}
          value={`${obj.level}`} 
          disabled={disabled}
          onChange={(changed: string) => {
            const new_obj = obj;
            new_obj.level = +changed;
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 2
        }}>
        <ToggleButtonBox 
          name="Basic"
          value={basic} 
          disabled={disabled}
          onToggle={() => {
            if (!basic) {
              const new_obj = obj;
              new_obj.rolls.flat.makeBasic();
              new_obj.rolls.count.makeBasic();
              new_obj.rolls.size.makeBasic();
              onChange(new_obj);
            }
            setBasic(!basic);
          }}
        />
      </Grid>
      { basic ? 
        <Grid
          size={{
            xs: 2
          }}>
          <StringBox 
            name="Dice Count"
            value={`${obj.rolls.count.base}`} 
            disabled={disabled}
            type="number"
            onChange={(value: string) => {
              const new_obj = obj;
              new_obj.rolls.count.base = +value;
              onChange(new_obj);
            }}
          />
        </Grid>
      :
        <Grid
          size={{
            xs: 2
          }}>
          <UpgradableNumberBox 
            name="Dice Count"
            value={obj.rolls.count} 
            disabled={disabled}
            attached_table={attached_table}
            onChange={(value: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.rolls.count = value;
              onChange(new_obj);
            }}
          />
        </Grid>
      }
      { basic ? 
        <Grid
          size={{
            xs: 2
          }}>
          <StringBox 
            name="Dice Size"
            value={`${obj.rolls.size.base}`} 
            disabled={disabled}
            type="number"
            onChange={(value: string) => {
              const new_obj = obj;
              new_obj.rolls.size.base = +value;
              onChange(new_obj);
            }}
          />
        </Grid>
      :
        <Grid
          size={{
            xs: 2
          }}>
          <UpgradableNumberBox 
            name="Dice Size"
            value={obj.rolls.size} 
            disabled={disabled}
            attached_table={attached_table}
            onChange={(value: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.rolls.size = value;
              onChange(new_obj);
            }}
          />
        </Grid>
      }
      { basic ? 
        <Grid
          size={{
            xs: 2
          }}>
          <StringBox 
            name="Flat"
            value={`${obj.rolls.flat.base}`} 
            disabled={disabled}
            type="number"
            onChange={(value: string) => {
              const new_obj = obj;
              new_obj.rolls.flat.base = +value;
              onChange(new_obj);
            }}
          />
        </Grid>
      :
        <Grid
          size={{
            xs: 2
          }}>
          <UpgradableNumberBox 
            name="Flat"
            value={obj.rolls.flat} 
            disabled={disabled}
            quick_options={ABILITY_SCORES as AbilityScoreType[]}
            attached_table={attached_table}
            onChange={(value: UpgradableNumber) => {
              const new_obj = obj;
              new_obj.rolls.flat = value;
              onChange(new_obj);
            }}
          />
        </Grid>
      }
      <Grid
        size={{
          xs: 2
        }}>
        <StringBox 
          name="Extra"
          value={obj.extra} 
          disabled={disabled}
          onBlur={(changed: string) => {
            const new_obj = obj;
            new_obj.extra = changed;
            onChange(new_obj);
          }}
        />
      </Grid>
      <Grid
        size={{
          xs: 1
        }}>
        { !disabled &&
          <Tooltip title={`Delete Potence`}>
            <Fab size="small" color="primary" style={{marginLeft: "8px"}}
              onClick={ () => {
                onDelete();
              }}>
              <DeleteForever/>
            </Fab>
          </Tooltip>
        }
      </Grid>
    </Grid>
  );
}
